// @ts-nocheck
/**
 * Apply a status-change optimistically to the workbench bootstrap so the
 * EBITDA Waterfall, KPI cards, and Punch List counts update instantly when
 * the analyst clicks Approve / Reject — before the backend has confirmed.
 *
 * Math:
 *   - Whether a row contributes to Adjusted EBITDA is determined by
 *     status === 'approved'. So toggling between {approved} and {!approved}
 *     adds or subtracts that row's impact_by_period from every total.
 *   - Going between two non-approved statuses (proposed → rejected, etc.)
 *     only changes counts_by_status; numeric totals stay put.
 *
 * Tier-2 caveat: when an analyst saves new inputs alongside an approve,
 * the server recomputes impact_by_period. Locally we don't have the new
 * impact, so totals will be slightly off for ~150ms until the server
 * response replaces the bootstrap. Acceptable visual jitter; users get
 * the immediate feedback they want.
 */

const num = (v) => Number(v || 0);

export function applyOptimisticStatusChange(bootstrap, adjustmentId, newStatus) {
  if (!bootstrap || !newStatus) return bootstrap;

  const adjustments = bootstrap.adjustments || [];
  const idx = adjustments.findIndex((a) => a.id === adjustmentId);
  if (idx === -1) return bootstrap;

  const adj = adjustments[idx];
  const oldStatus = adj.status;
  if (oldStatus === newStatus) return bootstrap;

  // Always update the row + counts_by_status.
  const newAdjustments = adjustments.slice();
  newAdjustments[idx] = { ...adj, status: newStatus };

  const oldCounts = bootstrap.summary?.counts_by_status || {};
  const newCounts = {
    ...oldCounts,
    [oldStatus]: Math.max(0, num(oldCounts[oldStatus]) - 1),
    [newStatus]: num(oldCounts[newStatus]) + 1,
  };

  const wasContributing = oldStatus === 'approved';
  const willContribute = newStatus === 'approved';

  if (wasContributing === willContribute) {
    // No change in EBITDA totals.
    return {
      ...bootstrap,
      adjustments: newAdjustments,
      summary: { ...bootstrap.summary, counts_by_status: newCounts },
    };
  }

  const multiplier = willContribute ? 1 : -1;
  const impact = adj.impact_by_period || {};

  // Per-period totals.
  const newPeriods = (bootstrap.periods || []).map((p) => {
    const delta = num(impact[p.label]) * multiplier;
    return {
      ...p,
      total_adjustments: String(num(p.total_adjustments) + delta),
      adjusted_ebitda: String(num(p.adjusted_ebitda) + delta),
    };
  });

  // Annual delta — sum of all per-period impacts, signed.
  const annualDelta =
    Object.values(impact).reduce((a, v) => a + num(v), 0) * multiplier;

  const oldByCategory = bootstrap.summary?.by_category || {};
  const newByCategory = {
    ...oldByCategory,
    [adj.category]: String(num(oldByCategory[adj.category]) + annualDelta),
  };

  const newTotalAdj = num(bootstrap.summary?.total_adjustments) + annualDelta;
  const newAdjustedEbitda = num(bootstrap.summary?.adjusted_ebitda) + annualDelta;
  const revenue = num(bootstrap.summary?.reported_revenue);
  const newMargin = revenue ? newAdjustedEbitda / revenue : null;

  return {
    ...bootstrap,
    adjustments: newAdjustments,
    periods: newPeriods,
    summary: {
      ...bootstrap.summary,
      total_adjustments: String(newTotalAdj),
      adjusted_ebitda: String(newAdjustedEbitda),
      adjusted_ebitda_margin: newMargin,
      by_category: newByCategory,
      counts_by_status: newCounts,
    },
  };
}
